import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X, Briefcase, FileText, Boxes } from "lucide-react";

import { v2Api } from "@/lib/v2Api";

/** Top-bar universal search. Debounced query → grouped dropdown. */
export default function UniversalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce
  useEffect(() => {
    if (!q.trim()) { setData(null); setError(null); return; }
    const t = setTimeout(async () => {
      setBusy(true); setError(null);
      try {
        const res = await v2Api.search(q.trim(), 10);
        setData(res);
      } catch (err) {
        setError(err?.response?.data?.detail ?? err.message ?? "Search failed");
        setData(null);
      } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const goto = (link) => {
    setOpen(false);
    setQ("");
    navigate(link);
  };

  const totalHits =
    (data?.projects?.length || 0) +
    (data?.rfp_chunks?.length || 0) +
    (data?.boq?.length || 0);

  return (
    <div ref={wrapRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search projects, RFPs, BOQ…   (try #priority)"
          className="w-full pl-9 pr-9 py-1.5 bg-white/10 hover:bg-white/15 focus:bg-white/20 border border-white/20 rounded-md text-sm text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-blue-300"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setData(null); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-xl max-h-[60vh] overflow-y-auto z-50 text-slate-800">
          {busy && !data && (
            <div className="px-3 py-3 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching…
            </div>
          )}
          {error && (
            <div className="px-3 py-3 text-sm text-rose-600">{error}</div>
          )}
          {data && totalHits === 0 && (
            <div className="px-3 py-4 text-sm text-slate-500 italic">
              No matches for "{q}". Try a project name, tag (#critical), or a phrase
              from the RFP body.
            </div>
          )}
          {data && data.projects?.length > 0 && (
            <ResultGroup
              icon={Briefcase}
              label="Projects"
              hits={data.projects}
              onPick={goto}
              renderTitle={(h) => `${h.project_code} — ${h.project_name}`}
            />
          )}
          {data && data.rfp_chunks?.length > 0 && (
            <ResultGroup
              icon={FileText}
              label="RFP excerpts"
              hits={data.rfp_chunks}
              onPick={goto}
              renderTitle={(h) =>
                `p.${h.page} · ${h.rfp_title || "RFP"} · ${h.project_code || ""}`
              }
            />
          )}
          {data && data.boq?.length > 0 && (
            <ResultGroup
              icon={Boxes}
              label="BOQ lines"
              hits={data.boq}
              onPick={goto}
              renderTitle={(h) => `${h.title} · ${h.project_code}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ icon: Icon, label, hits, onPick, renderTitle }) {
  return (
    <div className="border-b last:border-b-0">
      <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-400 bg-slate-50 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label} ({hits.length})
      </div>
      {hits.map((h, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onPick(h.deep_link)}
          className="w-full text-left px-3 py-2 hover:bg-blue-50 transition border-b last:border-b-0"
        >
          <p className="text-xs font-medium text-slate-800 truncate">{renderTitle(h)}</p>
          <p
            className="text-xs text-slate-500 line-clamp-2 mt-0.5"
            dangerouslySetInnerHTML={{
              // HTML-escape FIRST so RFP body text containing <script> etc.
              // can't inject — only THEN swap our ts_headline << >> markers
              // for actual <mark> tags.
              __html: (h.snippet || "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/&lt;&lt;/g, '<mark class="bg-amber-200">')
                .replace(/&gt;&gt;/g, "</mark>"),
            }}
          />
        </button>
      ))}
    </div>
  );
}
